import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useQueryClient } from "@tanstack/react-query";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("fh_token");
      if (!token) { setLoading(false); return; }
      const data = await api.getMe();
      setUser(data.user);
    } catch {
      api.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const data = await api.login({ email, password });
    api.setTokens(data.accessToken, data.refreshToken);
    setUser(data.user);
    return data;
  };

  const register = async (name, email, password) => {
    const data = await api.register({ name, email, password });
    api.setTokens(data.accessToken, data.refreshToken);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.logout();
      queryClient.clear(); 
    } catch { }
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};
